import { formatPoeLimitResponse } from '../status/status-poe-utils';

export const POE_CLASS_W = [15.4, 4, 7, 15.4, 30];
export const POE_USER_PWR_LIMIT = [30, 2, 4, 6, 8, 10, 12, 15, 20, 24, 18];

/** Default labels when firmware does not supply names (aligned with dashboard). */
export const POE_PORT_LABELS = [
  'P2',
  'P3',
  'P4',
  'P5',
  'P6',
  'P7',
  'P8',
  'P9',
  'P12',
  'P13',
  'P14',
  'P15',
  'P16',
  'P17',
  'P18',
  'P19',
];

export type PoeLimitMode =
  | { kind: 'single'; budget: number }
  | { kind: 'multi'; budgets: number[] };

export function parsePoeLimitMode(data: unknown): PoeLimitMode {
  if (data == null) return { kind: 'single', budget: 0 };
  if (Array.isArray(data) && data.length > 0) {
    const budgets = data.map((x) => {
      const l =
        typeof x === 'object' && x && 'limit' in x
          ? Number((x as { limit: unknown }).limit)
          : NaN;
      return Number.isFinite(l) ? l : 0;
    });
    return { kind: 'multi', budgets };
  }
  if (typeof data === 'object' && data && 'limit' in data) {
    const lim = Number((data as { limit: unknown }).limit);
    if (Number.isFinite(lim)) return { kind: 'single', budget: lim };
  }
  return { kind: 'single', budget: 0 };
}

export function formatLimitDisplay(data: unknown): string {
  return formatPoeLimitResponse(data);
}

export type PoePortForm = {
  name: string;
  enable: boolean;
  limit: number;
  limit_pwr: number;
  prio: number;
  fault: boolean;
};

export function cfgToForm(cfg: {
  name?: string;
  enable?: boolean;
  limit?: number;
  limit_pwr?: number;
  prio?: number;
  fault?: boolean;
}): PoePortForm {
  return {
    name: cfg.name ?? '',
    enable: Boolean(cfg.enable),
    limit: cfg.limit ?? 0,
    limit_pwr: cfg.limit_pwr ?? 0,
    prio: cfg.prio ?? 3,
    fault: Boolean(cfg.fault),
  };
}

function portContrib(r: PoePortForm): number {
  if (!r.enable) return 0;
  const limPort = POE_CLASS_W[r.limit] ?? 0;
  const lim2 = POE_USER_PWR_LIMIT[r.limit_pwr] ?? 0;
  return Math.min(limPort, lim2);
}

export type SumPart = { total: number; cap: number; over: boolean };

export function computeBudgetSumParts(
  rows: PoePortForm[],
  mode: PoeLimitMode,
): SumPart[] {
  if (mode.kind === 'single') {
    const total = rows.reduce((acc, r) => acc + portContrib(r), 0);
    return [{ total, cap: mode.budget, over: total > mode.budget }];
  }
  return mode.budgets.map((cap, j) => {
    const chunk = rows.slice(j * 8, j * 8 + 8);
    const total = chunk.reduce((acc, r) => acc + portContrib(r), 0);
    return { total, cap, over: total > cap };
  });
}

export const LIMIT_CLASS_OPTIONS = POE_CLASS_W.map((w, j) => ({
  value: String(j),
  label: `Class ${j} (${w}W)`,
}));

function buildNominalPowerOptions(): { value: string; label: string }[] {
  const arr = POE_USER_PWR_LIMIT;
  const rest = arr
    .slice(1)
    .sort((a, b) => a - b)
    .map((w) => ({
      value: String(arr.indexOf(w)),
      label: `${w}W`,
    }));
  return [{ value: '0', label: 'Class based' }, ...rest];
}

export const NOMINAL_PWR_OPTIONS = buildNominalPowerOptions();

export const PRIO_OPTIONS = [
  { value: '3', label: 'low' },
  { value: '2', label: 'high' },
  { value: '1', label: 'critical' },
];
